import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse, type Matter } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";
import {
  createMatterSettlementCounterV1,
  deleteMatterSettlementCounterV1,
  updateMatterSettlementCounterV1,
} from "@/lib/data/requests/matters/matter-settlement-counter";

export const useCreateMatterSettlementCounter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterSettlementCounterV1>[0]) =>
      createMatterSettlementCounterV1(data),
    onMutate: (data) => {
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              settlementCounters: [
                ...oldData.data.settlementCounters,
                {
                  ...data,
                  id: "optimistic",
                },
              ],
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toast.error("Error creating settlement counter");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Settlement counter created successfully");
    },
  });
};

export const useUpdateMatterSettlementCounter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterSettlementCounterV1>[0]) =>
      updateMatterSettlementCounterV1(data),
    onMutate: (data) => {
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              settlementCounters: oldData.data.settlementCounters.map(
                (counter) =>
                  counter.id === data.id
                    ? {
                        ...counter,
                        ...data,
                      }
                    : counter,
              ),
            },
          };
        },
      );
      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating settlement counter");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Settlement counter updated successfully");
    },
  });
};

export const useDeleteMatterSettlementCounter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterSettlementCounterV1>[0]) =>
      deleteMatterSettlementCounterV1(data),
    onMutate: (data) => {
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              settlementCounters: oldData.data.settlementCounters.filter(
                (counter) => counter.id !== data.id,
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error deleting settlement counter");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Settlement counter deleted successfully");
    },
  });
};
